import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, tokenStorage } from '@/api/axiosClient';
import { ROLES } from '@/types/auth';
import type { LoginResponse, Permission, UserDto } from '@/types/auth';

interface AuthContextValue {
  user: UserDto | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasPermission: (permission: Permission | Permission[]) => boolean;
  hasRole: (role: string) => boolean;
  login: (username: string, password: string, remember?: boolean) => Promise<UserDto>;
  logout: () => Promise<void>;
  /** Patches the cached current-user object in place — e.g. after a self-service change like a
   * profile photo upload — without needing a full re-login or a dedicated refetch. */
  updateUser: (user: UserDto) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    const bootstrap = async () => {
      if (!tokenStorage.getAccessToken()) {
        setIsLoading(false);
        return;
      }

      try {
        const { data } = await apiClient.get<UserDto>('/auth/me');
        setUser(data);
      } catch {
        tokenStorage.clear();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    void bootstrap();
  }, []);

  const login = useCallback(async (username: string, password: string, remember = false) => {
    const { data } = await apiClient.post<LoginResponse>('/auth/login', {
      username,
      password,
      rememberMe: remember,
    });

    // The QueryClient is a single app-lifetime instance shared across logins in the same tab (this
    // app navigates via the SPA router rather than a hard reload) — without clearing it here, a
    // second user in the same session could briefly see the previous user's cached company/data
    // before their own fetches land.
    queryClient.clear();
    tokenStorage.setTokens(data.accessToken, data.refreshToken, remember);
    setUser(data.user);
    return data.user;
  }, [queryClient]);

  const logout = useCallback(async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    if (refreshToken) {
      try {
        await apiClient.post('/auth/revoke-token', { refreshToken });
      } catch {
        // Best-effort revoke — local state is cleared regardless.
      }
    }

    tokenStorage.clear();
    setUser(null);
    queryClient.clear();
  }, [queryClient]);

  const hasRole = useCallback((role: string) => user?.roles.includes(role) ?? false, [user]);

  const hasPermission = useCallback(
    (permission: Permission | Permission[]) => {
      if (!user) return false;
      // SuperAdmin sees every screen, same unconditional bypass as Administrator — RolePermissions
      // only grants SuperAdmin whatever's been explicitly seeded (currently missing several newer
      // codes like GateExit.*), so relying on that list alone would leave gaps every time a new
      // permission is added without remembering to also grant it to SuperAdmin.
      if (user.roles.includes(ROLES.ADMINISTRATOR) || user.roles.includes(ROLES.SUPER_ADMIN)) return true;

      const required = Array.isArray(permission) ? permission : [permission];
      return required.some((code) => user.permissions.includes(code));
    },
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: Boolean(user), isLoading, hasPermission, hasRole, login, logout, updateUser: setUser }),
    [user, isLoading, hasPermission, hasRole, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
